// src/hooks/useZoneDepot.ts — SCE Issues
// Zone de dépôt de fichiers (glisser-déposer, PC). Deux niveaux :
//  - `fichierEnVol` : un fichier est glissé quelque part dans la fenêtre — la zone
//    s'annonce (« Déposez ici ») ;
//  - `survol` : il est au-dessus de la zone — elle réagit plus fort.
// Tant que le hook est monté, un fichier lâché à côté de la zone ne fait plus
// naviguer le navigateur vers lui (il ouvrait l'image ou le PDF dans l'onglet et
// la page déclarée était perdue). Ne réagit qu'aux vrais fichiers : glisser du
// texte ou une colonne du tableau ne déclenche rien.

import { useEffect, useRef, useState, type DragEvent } from 'react'

/** Le glissement transporte-t-il des fichiers (et pas du texte ou un lien) ? */
export function transporteDesFichiers(dt: Pick<DataTransfer, 'types'> | null | undefined): boolean {
  return !!dt && Array.from(dt.types ?? []).includes('Files')
}

export function useZoneDepot(
  surFichiers: (fichiers: File[]) => void,
  actif: boolean,
): {
  fichierEnVol: boolean
  survol: boolean
  propsZone: {
    onDragEnter: (e: DragEvent<HTMLElement>) => void
    onDragOver: (e: DragEvent<HTMLElement>) => void
    onDragLeave: (e: DragEvent<HTMLElement>) => void
    onDrop: (e: DragEvent<HTMLElement>) => void
  }
} {
  const [fichierEnVol, setFichierEnVol] = useState(false)
  const [survol, setSurvol] = useState(false)
  // dragenter / dragleave se déclenchent aussi sur les enfants : on compte.
  const dansFenetre = useRef(0)
  const dansZone = useRef(0)
  // Le dernier rappel, sans réabonner la fenêtre à chaque rendu.
  const rappel = useRef(surFichiers)
  rappel.current = surFichiers

  useEffect(() => {
    function fin(): void {
      dansFenetre.current = 0
      dansZone.current = 0
      setFichierEnVol(false)
      setSurvol(false)
    }
    function entrer(e: globalThis.DragEvent): void {
      if (!transporteDesFichiers(e.dataTransfer)) return
      dansFenetre.current += 1
      setFichierEnVol(true)
    }
    function quitter(e: globalThis.DragEvent): void {
      if (!transporteDesFichiers(e.dataTransfer)) return
      dansFenetre.current = Math.max(0, dansFenetre.current - 1)
      if (dansFenetre.current === 0) fin()
    }
    function survoler(e: globalThis.DragEvent): void {
      // Sans cela, le navigateur ouvre le fichier lâché hors de la zone.
      if (transporteDesFichiers(e.dataTransfer)) e.preventDefault()
    }
    function lacher(e: globalThis.DragEvent): void {
      if (transporteDesFichiers(e.dataTransfer)) e.preventDefault()
      fin()
    }
    window.addEventListener('dragenter', entrer)
    window.addEventListener('dragleave', quitter)
    window.addEventListener('dragover', survoler)
    window.addEventListener('drop', lacher)
    return () => {
      window.removeEventListener('dragenter', entrer)
      window.removeEventListener('dragleave', quitter)
      window.removeEventListener('dragover', survoler)
      window.removeEventListener('drop', lacher)
    }
  }, [])

  const propsZone = {
    onDragEnter: (e: DragEvent<HTMLElement>): void => {
      if (!actif || !transporteDesFichiers(e.dataTransfer)) return
      e.preventDefault()
      dansZone.current += 1
      setSurvol(true)
    },
    onDragOver: (e: DragEvent<HTMLElement>): void => {
      if (!transporteDesFichiers(e.dataTransfer)) return
      e.preventDefault()
      // Pièce jointe fermée (envoi en cours) : curseur « interdit ».
      e.dataTransfer.dropEffect = actif ? 'copy' : 'none'
    },
    onDragLeave: (e: DragEvent<HTMLElement>): void => {
      if (!actif || !transporteDesFichiers(e.dataTransfer)) return
      dansZone.current = Math.max(0, dansZone.current - 1)
      if (dansZone.current === 0) setSurvol(false)
    },
    onDrop: (e: DragEvent<HTMLElement>): void => {
      if (!transporteDesFichiers(e.dataTransfer)) return
      e.preventDefault()
      dansZone.current = 0
      setSurvol(false)
      if (!actif) return
      const fichiers = Array.from(e.dataTransfer.files)
      if (fichiers.length > 0) rappel.current(fichiers)
    },
  }

  return { fichierEnVol, survol, propsZone }
}
